import { type Component } from "vue";
import { type IVueListOptions, type ISwiperListOptions } from "@/Control/Vue/Lists/VueList/VueListOptions";
import { xs, lg, md, sm } from "@/utils/ScreenSize";
import { type SwiperOptions } from "swiper/types";
import { MiaPlaza } from "@/Reinforced.Typings";
import {
	ActionableMemberCard,
	CourseFinalizeItem,
	ResourceLink,
} from "@/chunks/LoggedIn";
import {
	ActiveModerator,
	AdminCouponCode,
	AdminCustomGame,
	AdminAccountPayment,
	AdminFriend,
	AdminLostPet,
	AdminMember,
	AdminMembershipChangeLogItem,
	AdminNewsletterContent,
	AdminProductPrice,
	AdminSubscriptionEmbedded,
	AdminTextResource,
	AdminUploadedImage,
	AnswerAttempt,
	BozoMemberData,
	CheckpointReportItem,
	ChildPenalty,
	CompetitionLeader,
	CompetitionWinner,
	DownloadEmail,
	FailedSubscriptionPauseItem,
	LessonFeedbackItem,
	MembershipReceiptRowItem,
	NumberOfAssessmentsToBeLockedInCourse,
	RejectedActivityListItem,
	SpecialUser,
	TopEarner,
	UserUploadedVideo,
	VirtualCurrencyTransaction,
} from "@/chunks/Admin";
import {
	AnsweredQuestion,
	CategoryAnsweredQuestions,
	Subject,
	TeamMember,
	Testimonial,
} from "@/chunks/Public";
import {
	ArcadeGame,
	DesignedItem,
	DesignedShopItem,
	DesignTemplate,
	DraftItem,
	MiniWorldHouse,
	Shape,
	ShopCompetitionItem,
} from "@/chunks/MyWorld";
import {
	Article,
	Gift,
	GuestBookEntry,
	Inventory,
	MedalItem,
	MiniMeClosetItem,
	NewspaperArticle,
	NewspaperEditionItem,
	NewspaperPreview,
	Team,
	TeamAchievements,
	Transaction,
} from "@/chunks/Community";
import {
	AssessmentAttempt,
	AssignCourse,
	AssignLesson,
	AssignUnit,
	AttendanceReportEntry,
	ConfigureCurriculumCourseEnrollment,
	ConfigureCurriculumSubject,
	ConfigureCustomBreak,
	ConfigureNoServiceDay,
	ConfigurePublicHoliday,
	CourseEnrollmentShift,
	GradebookAssignment,
	GradebookCourse,
	ReportCourse,
	ReportTask,
	ScheduleDay,
	ScheduleTask,
	ScheduleUnit,
	SearchScheduleTask,
	SelectableWeek,
} from "@/chunks/Parents";
import {
	LearningPathAssignedCourse,
	LearningPathCompletedCourse,
	LearningPathCompletedTask,
	LessonCard,
	LessonMenuTask,
	SubjectCard,
	Unit,
} from "@/chunks/LearningPath";
import {
	Filmmaker,
	Video,
	VideoBarDetailed,
} from "@/chunks/Videos";
import {
	FriendRequest,
	Message,
} from "@/chunks/MyFriends";
import {
	Member,
	StreakLeaderboardMember,
	RankedListItem,
	ShoutBoxItem,
} from "@/chunks/Students";

// Configure display of every list template here.
const Config: { [K: string]: IVueListOptions } = {
	"admin-custom-game": {
		backendOptions: {
			columnKeys: [
				"admincustomgame.commonid",
				"admincustomgame.play",
				"admincustomgame.status",
				"admincustomgame.localactions",
			],
		},
		component: AdminCustomGame,
		pageSize: 30,
	},
	"admin-membership-change-log": {
		backendOptions: {
			columnKeys: [
				"adminmembershipchangelog.created",
				"adminmembershipchangelog.membership",
				"adminmembershipchangelog.reason",
				"adminmembershipchangelog.subjects",
				"adminmembershipchangelog.communityaccess",
				"adminmembershipchangelog.values",
			],
		},
		component: AdminMembershipChangeLogItem,
		pageSize: 10,
	},
	"admin-account-payment": {
		backendOptions: {
			columnKeys: [
				"adminaccountpayment.type",
				"adminaccountpayment.product",
				"adminaccountpayment.creation",
				"adminaccountpayment.amount",
				"adminaccountpayment.braintreetransactionid",
				"adminaccountpayment.coupon",
			],
		},
		component: AdminAccountPayment,
		pageSize: 25,
	},
	"admin-friend": {
		backendOptions: {
			columnKeys: [
				"adminfriend.id",
				"adminfriend.name",
				"adminfriend.started",
			],
		},
		component: AdminFriend,
		pageSize: 25,
	},
	"answer-attempt": {
		backendOptions: {
			columnKeys: [
				"answerattempt.gameid",
				"answerattempt.gamename",
				"answerattempt.rate",
				"answerattempt.total",
			],
		},
		component: AnswerAttempt,
		pageSize: 25,
	},
	"special-user": {
		backendOptions: {
			columnKeys: [
				"specialuser.nameandnickname",
				"specialuser.specialusertype",
				"specialuser.email",
				"specialuser.deleteButton",
			],
		},
		component: SpecialUser,
		pageSize: 30,
	},
	"user-uploaded-video": {
		backendOptions: {},
		component: UserUploadedVideo,
		pageSize: 20,
	},
	"virtual-currency-transaction": {
		backendOptions: {
			columnKeys: [
				"transaction.time",
				"transaction.goldearned",
				"transaction.incomehistory",
				"transaction.type",
				"transaction.reason",
			],
		},
		component: VirtualCurrencyTransaction,
		pageSize: 50,
	},
	"top-earner": {
		backendOptions: {
			columnKeys: [
				"topearner.name",
				"topearner.income",
				"topearner.goldhistory",
				"whitespace",
				"topearner.cheatertatus",
				"whitespace",
			],
		},
		component: TopEarner,
		pageSize: 10,
	},
	"competition-winner": {
		backendOptions: {
			columnKeys: [
				"competitionwinner.winnername",
				"competitionwinner.type",
				"competitionwinner.date",
				"competitionwinner.moneyearned",
				"competitionwinner.prizename",
			],
		},
		component: CompetitionWinner,
		pageSize: 10,
	},
	"competition-leader": {
		backendOptions: {
			columnKeys: [
				"competitionleader.name",
				"competitionleader.moneyearned",
			],
		},
		component: CompetitionLeader,
		pageSize: 100,
	},
	"active-moderator": {
		backendOptions: {
			columnKeys: [
				"moderator.name",
				"moderator.locks",
				"whitespace",
			],
		},
		component: ActiveModerator,
		pageSize: 10,
	},
	"rejected-activity": {
		backendOptions: {
			distance: 200,
		},
		component: RejectedActivityListItem,
		pageSize: 20,
	},
	"lesson-feedback": {
		backendOptions: {
			columnKeys: [
				"lessonfeedback.student",
				"lessonfeedback.submitdate",
				"lessonfeedback.course",
				"lessonfeedback.unit",
				"lessonfeedback.lesson",
				"lessonfeedback.tasktype",
				"lessonfeedback.difficulty",
				"lessonfeedback.enjoyment",
				"lessonfeedback.additionalfeedback",
			],
		},
		component: LessonFeedbackItem,
		pageSize: 25,
	},
	"membership-receipt-row-item": {
		backendOptions: {
			columnKeys: [
				"membershipreceiptrowitem.student",
				"membershipreceiptrowitem.date",
				"membershipreceiptrowitem.amount",
				"membershipreceiptrowitem.type",
				"membershipreceiptrowitem.download",
			],
		},
		component: MembershipReceiptRowItem,
		pageSize: 10,
	},
	"admin-coupon-code": {
		backendOptions: {
			columnKeys: [
				"couponcode.code",
				"couponcode.start_date",
				"couponcode.expiration",
				"couponcode.visitors",
				"couponcode.discounts",
				"couponcode.usage",
				"whitespace"],
		},
		component: AdminCouponCode,
		pageSize: MiaPlaza.Control.Vue.Lists.AdminCouponCodeList.PAGE_SIZE,
	},
	"admin-lost-pet": {
		backendOptions: {
			columnKeys: [
				"lostpet.pet",
				"lostpet.owner_name",
				"lostpet.owner_id",
				"lostpet.give_back_btn",
			],
		},
		component: AdminLostPet,
		pageSize: MiaPlaza.Control.Vue.Lists.AdminLostPetList.PAGE_SIZE,
	},
	"admin-member": createSwiperOptions(AdminMember, {
		centerInsufficientSlides: false,
		slidesPerColumn: 10,
		slidesPerView: 2,
		speed: 100,
		mousewheel: {
			invert: true,
		},
	}, {
		[md]: {
			slidesPerView: 4,
		},
		[sm]: {
			slidesPerView: 3,
		},
	}),
	"admin-sale-price": {
		backendOptions: {
			columnKeys: [
				"pricing.start",
				"pricing.end",
				"pricing.price",
				"pricing.edit-sale",
			],
		},
		component: AdminProductPrice,
		pageSize: 20,
	},
	"admin-subscription-product-price": {
		backendOptions: {
			columnKeys: [
				"pricing.start",
				"pricing.end",
				"pricing.firstperiod",
				"pricing.price",
			],
			striped: true,
		},
		component: AdminProductPrice,
		pageSize: 20,
	},
	"admin-subscription-embedded": {
		backendOptions: {
			columnKeys: [
				"subscription.creation",
				"subscription.product",
				"subscription.nextbillingsubjects",
				"subscription.config",
				"subscription.start",
				"subscription.externalid",
				"subscription.action",
			],
			condensed: false,
		},
		component: AdminSubscriptionEmbedded,
		pageSize: MiaPlaza.Control.Vue.Lists.AdminEmbeddedSubscriptionList.PAGE_SIZE,
	},
	"admin-text-resource": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: AdminTextResource,
		pageSize: MiaPlaza.Control.Vue.Lists.AdminTextResourceList.PAGE_SIZE,
	},
	"article": createSwiperOptions(Article, {
		slidesPerView: 1,
	}, {
		[sm]: { slidesPerView: MiaPlaza.Control.Vue.Lists.ArticleList.PAGE_SIZE },
	}),
	"assign-unit": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: AssignUnit,
		pageSize: 10,
	},
	"assign-lesson": {
		backendOptions: {},
		component: AssignLesson,
		pageSize: 10,
	},
	"attendance-report": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: AttendanceReportEntry,
		pageSize: 8,
	},
	"bozo-member-data": {
		backendOptions: {
			columnKeys: [
				"bozomember.name",
				"bozomember.id",
				"bozomember.reviewer",
				"bozomember.creationdate",
				"bozomember.removebozoflaglink",
			],
		},
		component: BozoMemberData,
		pageSize: 40,
	},
	"configure-curriculum-subject": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: ConfigureCurriculumSubject,
		// pageSize 1 makes PreloadingItems ask for four subjects on first load
		pageSize: 1,
	},
	"gradebook-course": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: GradebookCourse,
		pageSize: 4,
	},
	"gradebook-assignment": {
		backendOptions: {
			columnKeys: [
				"gradebookassignment.lessonname",
				"gradebookassignment.type",
				"gradebookassignment.datecompleted",
				"gradebookassignment.grade",
				"gradebookassignment.attempts",
			],
		},
		component: GradebookAssignment,
		pageSize: 20,
	},
	"gradebook-assignment-pdf": {
		backendOptions: {
			columnKeys: [
				"gradebookassignment.lessonname",
				"gradebookassignment.type",
				"gradebookassignment.datecompleted",
				"gradebookassignment.grade",
				"gradebookassignment.attempts",
			],
			responsive: false,
		},
		component: GradebookAssignment,
		pageSize: 1000,
	},
	"configure-curriculum-course-enrollment": {
		backendOptions: {},
		component: ConfigureCurriculumCourseEnrollment,
		pageSize: 30,
	},
	"subject": createSwiperOptions(Subject, {
		autoHeight: false,
		centerInsufficientSlides: false,
		slidesPerView: 2,
	}, {
		[sm]: { slidesPerView: 4 },
		[md]: { slidesPerView: 7 },
	}),
	"lessons-subject": {
		backendOptions: {},
		component: SubjectCard,
		pageSize: 10,
	},
	"unit": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: Unit,
		pageSize: 3,
	},
	"download-email": {
		backendOptions: {
			columnKeys: [
				"downloademails.id",
				"downloademails.firstname",
				"downloademails.lastname",
				"downloademails.email",
				"downloademails.children",
				"downloademails.childrenlogins",
				"downloademails.purchasecount",
				"downloademails.lastpurchase",
				"downloademails.subscriptioncreation",
			],
		},
		component: DownloadEmail,
		pageSize: 100,
	},
	"filmmaker": createSwiperOptions(Filmmaker, {
		centerInsufficientSlides: false,
		slidesPerColumn: 1,
		slidesPerView: 3,
	}, {
		[sm]: {
			slidesPerView: 6.2,
		},
	}),
	"friend-request": createSwiperOptions(FriendRequest, {
		centerInsufficientSlides: false,
		slidesPerColumn: 5,
		slidesPerView: 1,
	}, {
		[sm]: { slidesPerView: 2 },
	}),
	"arcade-game": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: ArcadeGame,
		pageSize: MiaPlaza.Control.Vue.Lists.ArcadeGameList.PAGE_SIZE,
	},
	"give-gift": createSwiperOptions(Gift, {
		centerInsufficientSlides: false,
		// Set observer and observeParents to watch for visibility changes.
		// These properties will observe parent's container visibility and will refresh swiper when visibility changes.
		// If you remove these observers, swiper will become unresponsible until you change window size.
		observeParents: true,
		observer: true,
		slidesPerColumn: 3,
		slidesPerView: 2,
	}, {
		[sm]: { slidesPerView: 4 },
	}),
	"guestbook": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: GuestBookEntry,
		pageSize: 8,
	},
	"medal": createSwiperOptions(MedalItem, {
		slidesPerColumn: 1,
		slidesPerView: 5,
	}, {
		[xs]: { slidesPerView: 3, spaceBetween: 0 },
		[sm]: { slidesPerView: 5 },
	}),
	"inventory-item": createSwiperOptions(Inventory, {
		centerInsufficientSlides: false,
		slidesPerColumn: 3,
		slidesPerView: 2,
	}, {
		[sm]: {
			slidesPerView: 4,
		},
	}),
	"learning-path-assigned-course": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: LearningPathAssignedCourse,
		pageSize: 1024,
	},
	"learning-path-completed-course": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: LearningPathCompletedCourse,
		pageSize: 1024,
	},
	"learning-path-completed-task": createSwiperOptions(LearningPathCompletedTask, {
		centerInsufficientSlides: false,
		centeredSlides: true,
		displayAdjacentItems: false,
		slidesPerColumn: 1,
		slidesPerView: 1,
	}, {}),
	"lesson-card": createSwiperOptions(LessonCard, {
		centerInsufficientSlides: false,
		centeredSlides: false,
		displayAdjacentItems: true,
		slidesPerColumn: 1,
		slidesPerView: 1,
		spaceBetween: 8,
	}, {
		[sm]: {
			slidesPerView: 2,
		},
		[md]: {
			slidesPerView: 3,
		},
	}),
	"lesson-menu-task-desktop": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: LessonMenuTask,
		pageSize: 10,
	},
	"lesson-menu-task-mobile": createSwiperOptions(LessonMenuTask, {
		navigation: false,
		slidesPerView: "auto",
		maxSlidesPerView: 12,
		spaceBetween: 0,
	}, {}),
	"lesson-search": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: LessonCard,
		pageSize: 16,
	},
	"member": createSwiperOptions(Member, {
		slidesPerColumn: 1,
		slidesPerView: 1,
	}, {
		[480]: { slidesPerView: 2.2 },
		[sm]: { slidesPerView: 3.2 },
	}),
	"teamMember": createSwiperOptions(Member, {
		slidesPerColumn: 1,
		slidesPerView: 1,
	}, {
		[480]: { slidesPerView: 2 },
		[sm]: {
			slidesPerView: 2.5,
		},
	}),
	"member-block-list": createSwiperOptions(ActionableMemberCard, {
		slidesPerColumn: 1,
		slidesPerView: 1,
	}, {
		[sm]: { slidesPerView: 4 },
	}),
	"member-unfriend-list": createSwiperOptions(ActionableMemberCard, {
		slidesPerColumn: 1,
		slidesPerView: 1,
	}, {
		[sm]: { slidesPerView: 4 },
	}),
	"message": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: Message,
		pageSize: MiaPlaza.Control.Vue.Lists.MessageList.PAGE_SIZE,
	},
	"mini-world-draft": createSwiperOptions(DraftItem, {
		centerInsufficientSlides: false,
		slidesPerColumn: 2,
		slidesPerView: 2,
	}, {
		[sm]: { slidesPerView: 6 },
	}),
	"designed-item-more": createSwiperOptions(DesignedShopItem, {
		slidesPerColumn: 2,
		slidesPerView: 3,
		spaceBetween: 10,
		useTransitionOnEmptying: false,
	}, {
		[sm]: { slidesPerView: 5 },
		[md]: { slidesPerView: 6 },
		[lg]: { slidesPerView: 8 },
	}),
	"design-template": createSwiperOptions(DesignTemplate, {
		slidesPerColumn: 3,
		slidesPerView: 2,
		spaceBetween: 12,
		useTransitionOnEmptying: false,
	}, {
		[sm]: { slidesPerView: 3.2 },
		[md]: { slidesPerView: 4.2 },
		[lg]: { slidesPerView: 5.2 },
	}),
	"designed-item": createSwiperOptions(DesignedShopItem, {
		slidesPerColumn: 2,
		slidesPerView: 2,
		spaceBetween: 16,
		useTransitionOnEmptying: false,
	}, {
		[sm]: { slidesPerView: 3 },
		[md]: { slidesPerView: 4 },
		[lg]: { slidesPerView: 5 },
	}),
	"designed-item-vertical": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: DesignedShopItem,
		pageSize: 10,
	},
	"more-video": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: Video,
		pageSize: 10,
	},
	"explore-more-videos": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: Video,
		pageSize: 3,
	},
	"newsletter-content": {
		backendOptions: {
			columnKeys: [
				"admin_newsletter_is_on_parent_portal",
				"newslettercontent.creation_date",
				"newslettercontent.subject",
				"newslettercontent.status",
				"newslettercontent.progress",
				"newslettercontent.action",
				"admin_newsletter_duplicate",
				"newslettercontent.edit_review",
				"newslettercontent.delete",
			],
		},
		component: AdminNewsletterContent,
		pageSize: MiaPlaza.Control.Vue.Lists.AdminNewsletterContentList.PAGE_SIZE,
	},
	"newspaper-article": {
		backendOptions: {},
		component: NewspaperArticle,
		pageSize: MiaPlaza.Control.Vue.Lists.NewspaperArticleList.PAGE_SIZE,
	},
	"newspaper-preview": createSwiperOptions(NewspaperPreview, {
		autoHeight: true,
		slidesPerColumn: MiaPlaza.Control.Vue.Lists.NewspaperArticleList.PAGE_SIZE,
		slidesPerView: 1,
	}, {
		[sm]: { slidesPerView: 2 },
	}),
	"report": {
		backendOptions: {
			columnKeys: [],
		},
		component: ReportCourse,
		pageSize: 5,
	},
	"assessment-attempt": {
		backendOptions: {
			columnKeys: [
				"assessment-attempt.finished",
				"assessment-attempt.score",
			],
		},
		component: AssessmentAttempt,
		pageSize: 10,
	},
	"report-task": {
		backendOptions: {},
		component: ReportTask,
		pageSize: 10,
	},
	"configure-custom-break": {
		backendOptions: {},
		component: ConfigureCustomBreak,
		pageSize: 50,
	},
	"configure-public-holiday": {
		backendOptions: {},
		component: ConfigurePublicHoliday,
		pageSize: 5,
	},
	"configure-no-service-day": {
		backendOptions: {},
		component: ConfigureNoServiceDay,
		pageSize: 5,
	},
	"schedule-day": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: ScheduleDay,
		pageSize: 2,
	},
	"schedule-task": {
		backendOptions: {},
		component: ScheduleTask,
		pageSize: 5,
	},
	"schedule-unit": {
		backendOptions: {},
		component: ScheduleUnit,
		pageSize: 3,
	},
	"search-schedule-task": {
		backendOptions: {},
		component: SearchScheduleTask,
		pageSize: 10,
	},
	"selectable-week": createSwiperOptions(SelectableWeek, {
		displayAdjacentItems: false,
		mousewheel: {
			invert: true,
		},
		slidesPerColumn: 1,
		slidesPerView: 1,
		spaceBetween: 0,
	}, {
		[sm]: { slidesPerView: 2 },
		[md]: { slidesPerView: 3 },
		[lg]: { slidesPerView: 4 },
	}),
	"shape": createSwiperOptions(Shape, {
		autoHeight: false,
		slidesPerColumn: 1,
		centerInsufficientSlides: false,
		slidesPerView: 3,
		spaceBetween: 3,
	}, {
		[sm]: { slidesPerView: 7 },
		[lg]: { slidesPerView: 8.2 },
	}),
	"team-member": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: TeamMember,
		pageSize: 30,
	},
	"resource-link": {
		backendOptions: {},
		component: ResourceLink,
		pageSize: 100,
	},
	"shoutbox": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: ShoutBoxItem,
		pageSize: 15,
	},
	"team": createSwiperOptions(Team, {
		autoHeight: true,
		slidesPerColumn: MiaPlaza.Control.Vue.Lists.TeamList.PAGE_SIZE,
		slidesPerView: 1,
		displayAdjacentItems: false,
	}, {}),
	"testimonial": createSwiperOptions(Testimonial, {
		slidesPerView: 1,
		slidesOffsetAfter: 24,
		slidesOffsetBefore: 24,
		spaceBetween: 24,
	}, {
		[xs]: { spaceBetween: 24, slidesPerView: 1.2 },
		[sm]: { slidesPerView: 2 },
		[md]: { slidesPerView: 3.2 },
	}),
	"transaction": createSwiperOptions(Transaction, {
		autoHeight: true,
		slidesPerColumn: MiaPlaza.Control.Vue.Lists.TransactionList.PAGE_SIZE,
		slidesPerView: 1,
	}, {
		[sm]: { slidesPerView: 2 },
	}),
	"uploaded-image": {
		backendOptions: {
			columnKeys: [
				"uploadedimage.id",
				"uploadedimage.image",
				"uploadedimage.path_on_disk",
				"uploadedimage.date_of_creation",
				"uploadedimage.delete",
				"uploadedimage.open",
			],
		},
		component: AdminUploadedImage,
		pageSize: MiaPlaza.Control.Vue.Lists.AdminUploadedImageList.PAGE_SIZE,
	},
	"category-answered-questions": {
		backendOptions: {},
		component: CategoryAnsweredQuestions,
		pageSize: 10,
	},
	"answered-question": {
		backendOptions: {},
		component: AnsweredQuestion,
		pageSize: 30,
	},
	"video": createSwiperOptions(Video, {
		autoHeight: false,
		centerInsufficientSlides: false,
		slidesPerColumn: 1,
		slidesPerView: 1,
	}, {
		[sm]: {
			slidesPerView: 3.2,
		},
	}),
	"video-search-results": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: VideoBarDetailed,
		pageSize: 10,
	},
	"assign-course": {
		backendOptions: {},
		component: AssignCourse,
		pageSize: 30,
	},
	"mini-world-house": {
		backendOptions: {},
		component: MiniWorldHouse,
		pageSize: 6,
	},
	"team-achievements": {
		backendOptions: {},
		component: TeamAchievements,
		pageSize: 5,
	},
	"newspaper-edition": createSwiperOptions(NewspaperEditionItem, {
		// A vertical, page-at-a-time list of editions with up/down navigation
		// arrows. Each "slide" is a full page of editions; the swiper pages between
		// them rather than loading more and more.
		// autoHeight must stay false: a vertical swiper needs an explicit viewport
		// height (set in _Newspaper.scss), otherwise it collapses to 0 height.
		autoHeight: false,
		centerInsufficientSlides: false,
		direction: "vertical",
		displayAdjacentItems: false,
		slidesPerColumn: 12,
		slidesPerView: 1,
		spaceBetween: 0,
	}, {}),
	"streak-leaderboard": {
		backendOptions: {
			transitionName: "mia-ExpandableList-slide-transition",
		},
		component: StreakLeaderboardMember,
		pageSize: 20,
	},
	"ranked-list-item": {
		backendOptions: {},
		component: RankedListItem,
		pageSize: 20,
	},
	"design-item": createSwiperOptions(DesignedItem, {
		centerInsufficientSlides: false,
		slidesPerColumn: 3,
		slidesPerView: 2,
	}, {
		[sm]: { slidesPerView: 4 },
	}),
	"course-enrollment-shift": {
		backendOptions: {
			columnKeys: [
				"courseenrollmentshifteditor.course_name",
				"courseenrollmentshifteditor.current_dates",
				"courseenrollmentshifteditor.adjusted_dates",
			],
			hover: false,
		},
		component: CourseEnrollmentShift,
		pageSize: 100,
	},
	"checkpoint-report": {
		backendOptions: {
			columnKeys: [
				"checkpointreport.uuid",
				"checkpointreport.reporter",
				"checkpointreport.gradebook",
				"checkpointreport.createdat",
				"checkpointreport.reason",
				"checkpointreport.additionalinfo",
			],
		},
		component: CheckpointReportItem,
		pageSize: 25,
	},
	"failed-subscription-pause": {
		backendOptions: {
			columnKeys: [
				"failedsubscriptionpause.parent",
				"failedsubscriptionpause.student",
				"failedsubscriptionpause.startdate",
				"failedsubscriptionpause.enddate",
				"failedsubscriptionpause.refundamount",
				"failedsubscriptionpause.subscription",
				"failedsubscriptionpause.transaction",
				"failedsubscriptionpause.actions",
			],
			hover: false,
			condensed: false,
			striped: true,
		},
		component: FailedSubscriptionPauseItem,
		pageSize: 25,
	},
	"child-penalty": {
		backendOptions: {
			columnKeys: [
				"childPenalty.Type",
				"childPenalty.Status",
				"childPenalty.CreationDate",
				"childPenalty.EndDate",
				"childPenalty.Reason",
				"whitespace",
			],
		},
		component: ChildPenalty,
		pageSize: 40,
	},
	"shop-competition-item": {
		backendOptions: {},
		component: ShopCompetitionItem,
		pageSize: 10,
	},
	"course-finalize-item": {
		backendOptions: {},
		component: CourseFinalizeItem,
		pageSize: 100,
	},
	"mini-me-closet-item": {
		backendOptions: {
			distance: 1080 / 2,
		},
		component: MiniMeClosetItem,
		pageSize: 10,
	},
	"number-of-assessments-to-be-locked-in-course": {
		backendOptions: {
			columnKeys: [
				"numberofassessmentstobelockedincourse.coursetitle",
				"numberofassessmentstobelockedincourse.currentattemptlimit",
				"numberofassessmentstobelockedincourse.assessmentstobelocked",
			],
		},
		component: NumberOfAssessmentsToBeLockedInCourse,
		pageSize: 25,
	},
};
export default Config;

export function createSwiperOptions(
	component: Component,
	options: Partial<ISwiperListOptions>,
	breakpoints: { [K: number]: SwiperOptions }): IVueListOptions<ISwiperListOptions> {
	const backendOptions = {
		// Strangely, setting autoHeight to "true" makes some lists take height 0.
		// We are probably doing something wrong here but "false" seems to actually
		// produce a reasonable height for us.
		autoHeight: false,
		breakpointsInverse: true,
		centerInsufficientSlides: true,
		centeredSlides: false,
		direction: "horizontal",
		displayAdjacentItems: true,
		grabCursor: true,
		navigation: true,
		slidesPerColumn: 1,
		slidesPerView: 3,
		spaceBetween: 20,
		...options,
	} as ISwiperListOptions;
	backendOptions.breakpoints = breakpoints;
	return {
		backendOptions,
		component,
		pageSize: backendOptions.slidesPerColumn * getMaxSlidesPerView(backendOptions),
	};
}

export function getMaxSlidesPerView(options: ISwiperListOptions): number {
	if (options.maxSlidesPerView) {
		return Math.ceil(options.maxSlidesPerView);
	}
	const convertToNumber = (slidesPerView: number | "auto" | undefined) => {
		if (slidesPerView == "auto") {
			throw Error("not implemented: cannot convert 'auto' slides to a number for the preloading logic, set maxSlidesPerView explicitly");
		}
		if (slidesPerView === undefined) {
			return 1;
		}
		return slidesPerView;
	};

	return Math.ceil(
		Math.max(
			convertToNumber(options.slidesPerView),
			...Object.values(options.breakpoints || {})
				.map((config) => convertToNumber(config.slidesPerView)),
		),
	);
}
